const SAVED_LIST_FILTERS_KEY_PREFIX = 'contrak:listFilters:';

type ListFilters = Record<string, unknown>;

function getStorageKey(pageKey: string): string {
  return `${SAVED_LIST_FILTERS_KEY_PREFIX}${pageKey}`;
}

/** True when the saved value has the same shape (array vs. primitive type) as its default */
function getIsSameValueType(savedValue: unknown, defaultValue: unknown): boolean {
  if (Array.isArray(defaultValue)) {
    return Array.isArray(savedValue);
  }
  return typeof savedValue === typeof defaultValue;
}

/** Saved list filters merged over the defaults; unknown or wrongly-typed saved keys are ignored */
export function loadSavedListFilters<T extends ListFilters>(pageKey: string, defaultFilters: T): T {
  const mergedFilters = JSON.parse(JSON.stringify(defaultFilters)) as T;
  try {
    const rawSavedFilters = localStorage.getItem(getStorageKey(pageKey));
    if (!rawSavedFilters) {
      return mergedFilters;
    }
    const savedFilters = JSON.parse(rawSavedFilters);
    if (!savedFilters || typeof savedFilters !== 'object') {
      return mergedFilters;
    }
    Object.keys(defaultFilters).forEach((filterKey) => {
      if (getIsSameValueType(savedFilters[filterKey], defaultFilters[filterKey])) {
        (mergedFilters as ListFilters)[filterKey] = savedFilters[filterKey];
      }
    });
  } catch {
    // ignore private mode / blocked storage / corrupt JSON
  }
  return mergedFilters;
}

export function saveListFilters(pageKey: string, filters: ListFilters): void {
  try {
    localStorage.setItem(getStorageKey(pageKey), JSON.stringify(filters));
  } catch {
    // ignore private mode / blocked storage
  }
}

export function clearSavedListFilters(pageKey: string): void {
  try {
    localStorage.removeItem(getStorageKey(pageKey));
  } catch {
    // ignore private mode / blocked storage
  }
}

export function getIsListFiltersDifferentFromDefaults(
  filters: ListFilters,
  defaultFilters: ListFilters,
): boolean {
  return Object.keys(defaultFilters).some(
    (filterKey) => JSON.stringify(filters[filterKey]) !== JSON.stringify(defaultFilters[filterKey]),
  );
}
